import { inject, Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpStatusCode, HttpContextToken } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { NotificationService } from '@shared/services';
import { AUTH_ERROR_CODE } from '@shared/constants';
import { AuthActions } from '@store';
import { Router } from '@angular/router';

export const IS_EXTENDED_ERRORS = new HttpContextToken<boolean>(() => false);
export const CUSTOM_ERROR_MESSAGE = new HttpContextToken<string>(() => '');
export const SKIP_ERROR_NOTIFICATION = new HttpContextToken<boolean>(() => false);

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  notificationService = inject(NotificationService);
  store = inject(Store);
  router = inject(Router);

  private localNetworkAccessErrorShown = false;

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((err) => {
        // Requests that surface their own errors on-screen (e.g. password-expiry recovery): pass the
        // original HttpErrorResponse through untouched so the caller can read its status and body.
        if (request.context.get(SKIP_ERROR_NOTIFICATION)) {
          return throwError(() => err);
        }

        // PCI password-expiry at login: hand the body to the login effect so it can route the user to
        // set a new password. Must bypass the generic 403 "Permission Denied" toast below.
        if (err && err.status === HttpStatusCode.Forbidden && err.error?.errorCode === AUTH_ERROR_CODE.passwordExpired) {
          return throwError(() => err.error);
        }

        switch (err.status) {
          case 0:
            if (this.isLocalNetworkAccessError(request, err)) {
              this.showLocalNetworkAccessError();
            }
            break;

          case HttpStatusCode.Unauthorized:
            this.notificationService.displayInfo('Session Expired. Please login again to resume.', '', { timeOut: 60000 });
            this.store.dispatch(AuthActions.authLogout({ params: { isReturnUrl: true } }));
            break;

          case HttpStatusCode.Forbidden:
            this.notificationService.displayError('Permission Denied. Please contact your administrator.');
            break;

          default:
            break;
        }

        if (err && err.status === HttpStatusCode.ServiceUnavailable) {
          if (err.error === 'SYSTEM MAINTENANCE') {
            this.router.navigate(['/maintenance']);
          }
        }

        const isShellServiceError = request.url.split('/').reverse()[0] === 'command';
        let errorOutput = isShellServiceError ? err.error : (err && err.status !== 0 && err.status) || err.statusText;

        if (
          request.context.get(IS_EXTENDED_ERRORS) === true &&
          ![HttpStatusCode.Unauthorized, HttpStatusCode.Forbidden, HttpStatusCode.InternalServerError, HttpStatusCode.NotFound].includes(
            err.status,
          )
        ) {
          errorOutput = JSON.stringify(err.error);
        }

        return throwError(() => errorOutput);
      }),
    );
  }

  private isLocalNetworkAccessError(request: HttpRequest<unknown>, err: { status: number; statusText: string }): boolean {
    const isTenantInfoRequest = request.url.includes('/tenant-info');
    const isNetworkError = err.status === 0 && err.statusText === 'Unknown Error';
    return isTenantInfoRequest && isNetworkError;
  }

  private showLocalNetworkAccessError(): void {
    if (this.localNetworkAccessErrorShown) {
      return;
    }
    this.localNetworkAccessErrorShown = true;
    this.notificationService.displayWarning(
      'Local Network Access is required. Click the icon in the browser address bar and enable "Local network access", then refresh the page.',
      'Network Access Required',
      { timeOut: 0, extendedTimeOut: 0 },
    );
  }
}
